import { ThemeProvider, createTheme } from '@mui/material/styles';
import { makeStyles } from "@mui/styles";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import {
  AppBar,
  Autocomplete,
  Backdrop,
  Box,
  Button, CircularProgress, Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItemButton,
  Paper,
  Slide,
  Snackbar,
  TextField,
  Toolbar,
  Typography
} from '@mui/material';
import Alert from '@mui/material/Alert';
import _ from "lodash";
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { createFeatureObject, isUpdateFullySuccessful, isUpdatePartiallySuccessful } from '../../utils/FeatureHelper';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import ColorConstants from '../../defaults/ColorConstants';
import FieldNames from '../../defaults/FieldNames.json';
import { setActivities } from '../../redux/slices/activitiesSlice';
import { setActualsActivities } from '../../redux/slices/actualsActivitiesSlice';
import { setActualsTreatments } from '../../redux/slices/actualsTreatmentsSlice';
import { setTreatments } from '../../redux/slices/treatmentsSlice';
import ErrorService from '../../services/ErrorService';
import FeatureService from '../../services/FeatureService';
import GroupingService from '../../services/GroupingService';
import PlannedActualsService from '../../services/PlannedActualsService';
import BasicTextField from '../details/customPanels/BasicTextField';
import DraggableList from './DraggableList';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles({
  flexPaper: {
    flex: 1,
    padding: 16,
    backgroundColor: ColorConstants.LightGrayAlien,
    height: '100%'
  },
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    backgroundColor: ColorConstants.LightGrayAlien
  },
  listItem: {
    width: '550px'
  },
  draggingListItem: {
    background: 'rgb(235,235,235)',
    width: '550px'
  },
  formControl: {
    margin: 15,
    minWidth: 120,
    width: '100%'
  },
  select: {
    color: ColorConstants.GrayAlien,
    backgroundColor: ColorConstants.WhiteAlien,
    '&.MuiSelect-icon': {
      color: ColorConstants.GrayAlien,
    }
  },
  inputLabel: {
  },
  addButton: {
    backgroundColor: 'red'
  },
  autoComplete: {
    backgroundColor: 'green'
  },
  disabled: {
    backgroundColor: ColorConstants.GrayAlien
  }
});

const theme = createTheme({
  overrides: {
    MuiOutlinedInput: {
      root: {
        backgroundColor: ColorConstants.WhiteAlien,
      },
    },
    MuiSelect: {
      icon: {
        color: ColorConstants.GrayAlien
      },
      root: {
      }
    },
    MuiInputLabel: {
      root: {
      }
    }
  }
});

export default function GroupingsDialog({userInfo, setFeatureGroupItems, setFeatureGroupIds, featureGroupIds, featureGroupItems, 
  displayGroupingsClose, groupingsOpen, setAssociatedCount, isChangesMade, setIsChangesMade, snackbarWarningOpen, 
  setSnackbarWarningOpen, treatments, activities, currentFeature, setCurrentFeature, featureGroups, actualTreatments, 
  actualActivities }) {
  const classes = useStyles();
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState();
  const containerRef = useRef(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [nextSelectedIndex, setNextSelectedIndex] = useState(null);
  const [nextSelectedGroupId, setNextSelectedGroupId] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const dispatch = useDispatch();
  const [listItemSelected, setListItemSelected] = useState(false);
  const [featureItems, setFeatureItems] = useState([]);
  const [selectionValue, setSelectionValue] = useState("");
  const [groupName, setGroupName] = useState("");
  const [addOptions, setAddOptions] = useState([]);
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    if(groupingsOpen){
      const treatmentAttrs = treatments.map(x => (x && x.attributes) ? x.attributes : x);
      const activityAttrs = activities.map(x => (x && x.attributes) ? x.attributes : x);
      const actualTreatmentAttrs = actualTreatments.map(x => (x && x.attributes) ? x.attributes : x);
      const actualActivityAttrs = actualActivities.map(x => (x && x.attributes) ? x.attributes : x);
      const allItems = treatmentAttrs.concat(activityAttrs).concat(actualTreatmentAttrs).concat(actualActivityAttrs);
      setFeatureItems(allItems);
    }
  }, [groupingsOpen, activities, treatments, actualTreatments, actualActivities])

  useEffect(() => {
    if(addOptions && addOptions.length > 0){
      let sortedItems = addOptions.sort((a,b) => (a[FieldNames.featureFields.name] > b[FieldNames.featureFields.name]) ? 1 : -1);
      setAddOptions(sortedItems)
    }
  }, [addOptions])

  const getAddOptions = (existingGroupItems) => {
    //Want this to run only on initial load
    let remainingOptions = [];
    let existingFeatureGroupItems = featureGroupItems;
    if(existingGroupItems){
      existingFeatureGroupItems = existingGroupItems
    }

    if(existingFeatureGroupItems && existingFeatureGroupItems.length > 0){
      //Already by group
      let featureGroupItemsIds = existingFeatureGroupItems?.map(x => x[FieldNames.treatmentActivityGroupItems.joinID]);
      if(featureGroupItemsIds?.length > 0) {
        remainingOptions = featureItems.filter(x => !featureGroupItemsIds.includes(x[FieldNames.featureFields.id]))
        return remainingOptions
      } else {
        return featureItems
      }
    }
  }

  const updateMultipleAfterRemoval = (selectedFeatures, isMoreLeft, groupItems) => {
    //Updating the group count on features
    const features = actualTreatments.concat(treatments).concat(actualActivities).concat(activities);
    const featureIds = selectedFeatures.map(x => x[FieldNames.featureFields.id]);
    let updatedItems = [];
    let tempFeatures;
    if(isMoreLeft && groupItems && groupItems.length > 0){
      tempFeatures = features.map(feature => {
        let featureAttr = Object.assign({}, feature);
        groupItems.forEach(groupItem => {
          if(groupItem[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID] === featureAttr[FieldNames.featureFields.id] && 
            groupItem[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID] === selectedGroupId){
            let count = featureAttr[FieldNames.featureFields.groupCount];
            if(!featureAttr[FieldNames.featureFields.groupCount]){
              count = 0;
            }
            if(count === 1){
              featureAttr[FieldNames.featureFields.hasGroup] = FieldNames.yesNo.no;
            }
            featureAttr[FieldNames.featureFields.groupCount] = (count && count > 0) ? count - 1 : 0;
            updatedItems.push(featureAttr);
          }
        })
        
        return featureAttr
      })
    } else {
      tempFeatures = features.map(feature => {
        let featureAttr = Object.assign({}, feature);
        if(featureIds && featureIds.includes(featureAttr[FieldNames.featureFields.id])){
          let count = featureAttr[FieldNames.featureFields.groupCount];
          if(!featureAttr[FieldNames.featureFields.groupCount]){
            count = 0;
          }
          if(count === 1){
            featureAttr[FieldNames.featureFields.hasGroup] = FieldNames.yesNo.no;
          }
          featureAttr[FieldNames.featureFields.groupCount] = count - 1;
          updatedItems.push(featureAttr);
        }
        return featureAttr
      })
    }
    
    FeatureService.updateMultipleFeatures(updatedItems).then(resp => {
      let isAtLeastPartiallySuccessful = isUpdatePartiallySuccessful(resp);
      let isFullySuccessful = isUpdateFullySuccessful(resp);
      if(isAtLeastPartiallySuccessful || isFullySuccessful){
        const tempActualTreatmentFeatures = tempFeatures.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment);
        dispatch(setActualsTreatments(tempActualTreatmentFeatures));

        const tempActualActivityFeatures = tempFeatures.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.actualActivity);
        dispatch(setActualsActivities(tempActualActivityFeatures));

        const tempTreatmentFeatures = tempFeatures.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.treatment);
        dispatch(setTreatments(tempTreatmentFeatures));

        const tempActivityFeatures = tempFeatures.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.activity);
        dispatch(setActivities(tempActivityFeatures));
      }
    }).catch(error => {
      ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
    });
  }

  const resetGroupCountCurrentFeature = () => {
    if(currentFeature && currentFeature.attributes){
      //let tempCurrentFeature = Object.assign({}, currentFeature);
      let tempCurrentFeature = _.cloneDeep(currentFeature);
      let newCount = tempCurrentFeature.attributes[FieldNames.featureFields.groupCount] - 1;
      tempCurrentFeature.attributes[FieldNames.featureFields.groupCount] = newCount;
      if(newCount === 0){
        tempCurrentFeature.attributes[FieldNames.featureFields.hasGroup] = FieldNames.yesNo.no;
      } else {
        tempCurrentFeature.attributes[FieldNames.featureFields.hasGroup] = FieldNames.yesNo.yes;
      }
      setCurrentFeature(tempCurrentFeature);
    }
  }

  const removeFromRemainingItems = (remainingFeatures, remainingGroupItems, feature) => {
    //Everything is still in terms of object id instead of id - That's ok since we want the group item object id for deletion
    let remainingGroupItemsObjectIds = remainingGroupItems.map(x => x[FieldNames.treatmentActivityGroupItems.objectID]);
    let remainingGroupItemsObjectIdsToDelete = [];
    if(remainingGroupItemsObjectIds?.length > 0 && featureGroupItems?.length > 0){
      remainingGroupItemsObjectIdsToDelete = featureGroupItems.filter(x => !remainingGroupItemsObjectIds.includes(x[FieldNames.treatmentActivityGroupItems.objectID]) && x[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID] === selectedGroupId)
    }
    
    if(remainingFeatures.length === 1){
      if(selectedFeatures && selectedFeatures.length > 0){
        GroupingService.deleteAllItemsFromGroup(selectedGroupId).then(() => {
          GroupingService.deleteGroup(selectedGroupId).then(() => {
            //Group is being deleted completely so subtract 1
            setAddOptions([]);
            setSelectionValue("");
            setListItemSelected(false);
            resetGroupCountCurrentFeature();
            updateMultipleAfterRemoval(selectedFeatures, false, null);

            const remainingGroupIds = featureGroupIds.filter(x => x !== selectedGroupId);
            setFeatureGroupIds(remainingGroupIds);
            const remainingFeatureGroupItems = featureGroupItems.filter(x => x[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID] !== selectedGroupId);
            setFeatureGroupItems(remainingFeatureGroupItems);
            if(setAssociatedCount){
              setAssociatedCount(remainingGroupIds.length);
            }
            setSnackbarMessage("Association removed successfully.");
            setSnackbarOpen(true);
            setIsChangesMade(false);
            
            setSelectedFeatures([{}])
          });
        }).catch(error => {
          ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
        });
      }
    } else {
      if(remainingGroupItemsObjectIdsToDelete && remainingGroupItemsObjectIdsToDelete.length > 0){
        let groupItemObjectIdsToDelete = remainingGroupItemsObjectIdsToDelete.map(x => x[FieldNames.treatmentActivityGroupItems.objectID]);
        GroupingService.deleteItemsFromGroup(groupItemObjectIdsToDelete, selectedGroupId).then(() => {
          remainingGroupItemsObjectIdsToDelete.forEach((groupItem) => {
          
          });
          if(selectedFeatures && selectedFeatures.length > 0){
            updateMultipleAfterRemoval(selectedFeatures, true, remainingGroupItemsObjectIdsToDelete);
          }

          remainingFeatures.forEach((item) => {
            if(item[FieldNames.featureFields.class] === FieldNames.classTypes.treatment){
              item["EntityType"] = FieldNames.entityTypes.treatment;
            } else if(item[FieldNames.featureFields.class] === FieldNames.classTypes.activity){
              item["EntityType"] = FieldNames.entityTypes.activity;
            } else if(item[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment){
              item["EntityType"] = FieldNames.entityTypes.actualTreatment;
            } else if(item[FieldNames.featureFields.class] === FieldNames.classTypes.actualActivity){
              item["EntityType"] = FieldNames.entityTypes.actualActivity;
            }
          })
          setSelectedFeatures(remainingFeatures);
          setFeatureGroupItems(remainingGroupItems);
          setIsChangesMade(true);
          let allOptions = getAddOptions(remainingGroupItems);
          setAddOptions(allOptions);
          setSnackbarMessage("Association Feature removed successfully.");
          setSnackbarOpen(true);
          //Need to update remaining items to reset the sequence
        }).catch(error => {
          ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
        });
      }
    }
  }

  const handleRemoval = (e, feature) => {
    //feature is the feature we are removing
    const featureId = feature[FieldNames.featureFields.id];
    //remainingFeatures is the features(Estimated Treatment, Estimated Activity, ...) left once we get rid of the one we want to remove
    let remainingFeatures = selectedFeatures.filter(x => x.ID !== featureId);
    //The remaining Group items once we remove the one we are deleting
    let remainingGroupItems = featureGroupItems.filter(x => x[FieldNames.treatmentActivityGroupItems.joinID] !== featureId || 
      (x[FieldNames.treatmentActivityGroupItems.joinID] === featureId && x[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID] !== selectedGroupId));

    //Determine if the feature being deleted is an actual or a treatment
    const isFeatureTreatment = (feature[FieldNames.featureFields.class] === FieldNames.classTypes.treatment || feature[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment);
    const isFeatureActual = (feature[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment || feature[FieldNames.featureFields.class] === FieldNames.classTypes.actualActivity)
    
    //We have to check for an entry in the join table
    PlannedActualsService.getPlannedActualsByJoinIDs(feature[FieldNames.featureFields.joinFeature], [feature[FieldNames.featureFields.joinID]], isFeatureActual).then(items => {
      let connectedItemsToRemove = [];
      if(items?.length > 0){
        //Entry was found
        items.forEach(item => {
          let itemAttr = item;
          if(item?.attributes){
            itemAttr = item.attributes;
          }
          if(isFeatureTreatment){
            if(isFeatureActual){
              //Actual Treatment - Get the opposite: Estimated Treatment
              connectedItemsToRemove.push(itemAttr[FieldNames.estimatedActuals.joinIDE]);
            } else {
              //Estimated Treatment - Get the opposite: Actual Treatment
              connectedItemsToRemove.push(itemAttr[FieldNames.estimatedActuals.joinIDA]);
            }
            
          } else {
            if(isFeatureActual){
              //Actual Activity - Get the opposite: Estimated Activity
              connectedItemsToRemove.push(itemAttr[FieldNames.estimatedActuals.joinIDE]);
            } else {
              //Estimated Activity - Get the opposite: Actual Activity
              connectedItemsToRemove.push(itemAttr[FieldNames.estimatedActuals.joinIDA]);
            }
          }
        });
        if(remainingFeatures?.length > 0 && connectedItemsToRemove?.length > 0){
          //Remove the connected feature
          remainingFeatures = remainingFeatures.filter(x => !connectedItemsToRemove.includes(x[FieldNames.featureFields.id]));
        }
        let finalGroupItems = [];
        if(connectedItemsToRemove?.length > 0 && remainingGroupItems?.length > 0){
          //There is an attached item - See if it's in the list
          finalGroupItems = remainingGroupItems.filter(x => (x[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID] === selectedGroupId && !connectedItemsToRemove.includes(x[FieldNames.treatmentActivityGroupItems.id])) || 
          x[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID] !== selectedGroupId)
        } else {
          finalGroupItems = remainingGroupItems;
        }
        removeFromRemainingItems(remainingFeatures, finalGroupItems, feature);
      } else {
        //No entry found
        removeFromRemainingItems(remainingFeatures, remainingGroupItems, feature);
      }
    }).catch(error => {
      ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
    });
  }

  const createGroupingObjects = (groupedItems, filteredItems, isFeatureTreatment, isFeatureActual) => {
    let features = [];
    let featureFields; 
    let treatmentActivityGroupItemsFieldsFeatureIDs;
    let featureId;
    if(isFeatureTreatment) {
      if(isFeatureActual){
        featureFields = FieldNames.featureFields;
        treatmentActivityGroupItemsFieldsFeatureIDs = FieldNames.treatmentActivityGroupItems.entityID;
        featureId = FieldNames.featureFields.id;
      } else {
        featureFields = FieldNames.featureFields;
        treatmentActivityGroupItemsFieldsFeatureIDs = FieldNames.treatmentActivityGroupItems.entityID ?? FieldNames.treatmentActivityGroupItems.joinID;
        featureId = FieldNames.featureFields.id;
      }
    } else {
      if(isFeatureActual){
        featureFields = FieldNames.featureFields;
        treatmentActivityGroupItemsFieldsFeatureIDs = FieldNames.treatmentActivityGroupItems.entityID;
        featureId = FieldNames.featureFields.id;
      } else {
        featureFields = FieldNames.featureFields;
        treatmentActivityGroupItemsFieldsFeatureIDs = FieldNames.treatmentActivityGroupItems.entityID ?? FieldNames.treatmentActivityGroupItems.joinID;
        featureId = FieldNames.featureFields.id;
      }
    }
    groupedItems.forEach((grpItem) => {
      const filteredGroupItem = filteredItems.filter(x => x[treatmentActivityGroupItemsFieldsFeatureIDs] === grpItem[featureFields.objectID])?.[0];
      if(grpItem && filteredGroupItem){
        const obj = createFeatureObject(grpItem, filteredGroupItem[FieldNames.treatmentActivityGroupItems.sequenceNumber]);
        features.push(obj);
      } else {
        const obj = createFeatureObject(grpItem, 0);
        features.push(obj);
      }
    });
    return features;
  }

  const getFeatureItems = async (
    sortedItemsBySelectedGroup,
    setSelectedFeatures,
    isTreatment,
    isActual,
    userInfo,
    createGroupingObjects
  ) => {
    try {
      if (sortedItemsBySelectedGroup.length > 0) {
        let polygonGroupItems = sortedItemsBySelectedGroup.filter(
          (x) =>
            x[FieldNames.treatmentActivityGroupItems.joinFeature] ===
            FieldNames.joinFeatureValues.polygon
        );
        let lineGroupItems = sortedItemsBySelectedGroup.filter(
          (x) =>
            x[FieldNames.treatmentActivityGroupItems.joinFeature] ===
            FieldNames.joinFeatureValues.line
        );
        let pointGroupItems = sortedItemsBySelectedGroup.filter(
          (x) =>
            x[FieldNames.treatmentActivityGroupItems.joinFeature] ===
            FieldNames.joinFeatureValues.point
        );
        setShowLoader(true);
        const polygonResp = FeatureService.getFeaturesByJoinValues(
          polygonGroupItems.map(
            (x) => x[FieldNames.treatmentActivityGroupItems.joinID]
          ),
          FieldNames.joinFeatureValues.polygon
        );
        const lineResp = FeatureService.getFeaturesByJoinValues(
          lineGroupItems.map(
            (x) => x[FieldNames.treatmentActivityGroupItems.joinID]
          ),
          FieldNames.joinFeatureValues.line
        );
        const pointResp = FeatureService.getFeaturesByJoinValues(
          pointGroupItems.map(
            (x) => x[FieldNames.treatmentActivityGroupItems.joinID]
          ),
          FieldNames.joinFeatureValues.point
        );
        let respValues = await Promise.all([polygonResp, lineResp, pointResp]);
        setShowLoader(false);
        let combinedResp = [
          ...(respValues?.[0] ?? []),
          ...(respValues?.[1] ?? []),
          ...(respValues?.[2] ?? []),
        ];
        let createdFeatures = [];
        if (
          sortedItemsBySelectedGroup &&
          sortedItemsBySelectedGroup.length > 0
        ) {
          createdFeatures = createGroupingObjects(
            combinedResp,
            sortedItemsBySelectedGroup,
            isTreatment,
            isActual
          );
          if (createdFeatures && createdFeatures.length > 0) {
            setSelectedFeatures((selectedFeatures) => [
              ...selectedFeatures,
              ...createdFeatures,
            ]);

            const dropdownOptions = getAddOptions(sortedItemsBySelectedGroup);
            if (dropdownOptions && dropdownOptions.length > 0) {
              setAddOptions(dropdownOptions);
            }
          }
        }
      }
    } catch (error) {
      ErrorService.addErrorLogEntry(
        userInfo[FieldNames.userFields.nifcUsername],
        error
      );
    }
  };

  const handleListItemClick = (index, groupId, isChanged) => {
    //Need to clear the list on the right every time a group is clicked or we will get more than are actually in the group
    setSelectedFeatures([])
    setListItemSelected(true);
    if(isChanged){
      setNextSelectedIndex(index);
      setNextSelectedGroupId(groupId);
      setSnackbarWarningOpen(true);
    } else {
      setIsInitialLoad(true);
      setNextSelectedIndex(null);
      setNextSelectedGroupId(null);
      setSelectedGroupId(groupId);
      const currentGroup = featureGroups.filter(x => x[FieldNames.treatmentActivityGroups.id] === groupId);
      if(currentGroup && currentGroup.length > 0){
        setGroupName(currentGroup[0][FieldNames.treatmentActivityGroups.treatmentActivityGroupName] ? currentGroup[0][FieldNames.treatmentActivityGroups.treatmentActivityGroupName] : '');
      }
      
      const itemsBySelectedGroup = featureGroupItems.filter(x => x[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID] === groupId);
      const sortedItemsBySelectedGroup = itemsBySelectedGroup.sort((a, b) => (a[FieldNames.treatmentActivityGroupItems.sequenceNumber] > b[FieldNames.treatmentActivityGroupItems.sequenceNumber]) ? 1 : -1);
      if(sortedItemsBySelectedGroup?.length > 0){
        getFeatureItems(sortedItemsBySelectedGroup, setSelectedFeatures, true, true, userInfo, createGroupingObjects);
      } else {
        const dropdownOptions = getAddOptions([]);
        if(dropdownOptions && dropdownOptions.length > 0){
          setAddOptions(dropdownOptions);
        }
      }
    }
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const createNewTreatmentActivityGroupItem = (filteredItems, selectedVal, hasActual) => {
    let newTreatmentActivityGroupItem = {};
    let featureType;
    if(selectedVal){
      if(selectedVal[FieldNames.featureFields.class] === FieldNames.classTypes.treatment){
        featureType = FieldNames.entityTypes.treatment;
      } else if(selectedVal[FieldNames.featureFields.class] === FieldNames.classTypes.activity){
        featureType = FieldNames.entityTypes.activity;
      } else if(selectedVal[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment){
        featureType = FieldNames.entityTypes.actualTreatment;
      } else if(selectedVal[FieldNames.featureFields.class] === FieldNames.classTypes.actualActivity){
        featureType = FieldNames.entityTypes.actualActivity;
      }

      const currentGroup = featureGroups?.filter(x => x[FieldNames.treatmentActivityGroups.id] === selectedGroupId);
      newTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.joinID] = selectedVal[FieldNames.featureFields.id];
      newTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.isArchived] = 'No';
      newTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupItemsID] = uuidv4();
      newTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.globalID] = uuidv4();
      newTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID] = selectedGroupId;
      newTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupID] = currentGroup?.length > 0 ? currentGroup[0][FieldNames.treatmentActivityGroups.globalID] : selectedGroupId;
      newTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.entityID] = `${selectedVal[FieldNames.featureFields.objectID]}`;
      newTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.id] = selectedVal[FieldNames.featureFields.id];
      newTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.entityType] = featureType;
      newTreatmentActivityGroupItem[FieldNames.featureFields.hasActual] = hasActual ? FieldNames.yesNo.yes : selectedVal[FieldNames.featureFields.hasActual];
      newTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.sequenceNumber] = filteredItems[filteredItems.length - 1][FieldNames.treatmentActivityGroupItems.sequenceNumber] + 1;
      newTreatmentActivityGroupItem["Name"] = selectedVal[FieldNames.featureFields.name];
    }
    
    return newTreatmentActivityGroupItem;
  }

  const getTreatmentActivityGroupItems = (feature, i) => {
    let groupItemsToSave = [];
    groupItemsToSave = featureGroupItems.filter(x => x[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID] === selectedGroupId && 
      x[FieldNames.treatmentActivityGroupItems.joinID] === feature[FieldNames.featureFields.joinID] && 
      x[FieldNames.treatmentActivityGroupItems.joinFeature] === feature[FieldNames.featureFields.joinFeature]);

    let groupItemToSave;
    if(groupItemsToSave?.length > 0){
      groupItemToSave = groupItemsToSave[0];
      if(groupItemToSave){
        groupItemToSave[FieldNames.treatmentActivityGroupItems.sequenceNumber] = i + 1;
        groupItemToSave["Name"] = feature.Name;
      }
    }
    return groupItemToSave
  }

  const updateGroupItems = (adjustedGroupItemsForSaving, newItemsToDisplay) => {
    GroupingService.updateItemForGroup(adjustedGroupItemsForSaving).then((resp) => {
      if(resp && resp.updateResults && resp.updateResults.length > 0 && resp.updateResults[0] &&  resp.updateResults[0].success){
        setSnackbarMessage("Association changes saved successfully.");
        setSnackbarOpen(true);
        setIsChangesMade(false);
        //Remove the items we just updated
        let tempFeatureGroupItems = _.cloneDeep(featureGroupItems);
        let filteredOutFeatureGroupItems = tempFeatureGroupItems.filter(x => x[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID] !== selectedGroupId);
        let allAddedBack = filteredOutFeatureGroupItems.concat(newItemsToDisplay);
        //Add all of them back
        setFeatureGroupItems(allAddedBack);
      } else {
        setSnackbarMessage("An error occurred. Please reload and try again");
        setSnackbarOpen(true);
        setIsChangesMade(true);
      }
    }).catch(error => {
      ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
    });
  }

  const handleSaveGrouping = () => {
    if(selectedFeatures.length > 0){
      let newItemsToDisplay = [];
      let adjustedGroupItemsForSaving = [];
      selectedFeatures.forEach((feature, i) => {     
        const itemAttributes = getTreatmentActivityGroupItems(feature, i);
        if(itemAttributes){
          const groupItem = 
          { 
              attributes: itemAttributes
          };
          adjustedGroupItemsForSaving.push(groupItem);
          newItemsToDisplay.push(itemAttributes);
        }
      });
      const currentGroups = featureGroups.filter(x => x[FieldNames.treatmentActivityGroups.id] === selectedGroupId);
      if(currentGroups?.length > 0){
        let currentGroup = currentGroups[0];
        if(currentGroup && groupName !== currentGroup[FieldNames.treatmentActivityGroups.treatmentActivityGroupName]){
          currentGroup[FieldNames.treatmentActivityGroups.treatmentActivityGroupName] = groupName;
          GroupingService.updateGroupName(currentGroup[FieldNames.treatmentActivityGroups.objectID], groupName).then(groupNameResp => {
            if(groupNameResp && groupNameResp.updateResults && groupNameResp.updateResults.length > 0 && groupNameResp.updateResults[0].success){
              updateGroupItems(adjustedGroupItemsForSaving, newItemsToDisplay);
            } else {
              setSnackbarMessage("An error occurred while updating the Group Name. Please reload and try again");
              setSnackbarOpen(true);
              setIsChangesMade(true);
              updateGroupItems(adjustedGroupItemsForSaving, newItemsToDisplay);
            }
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          });
        } else {
          updateGroupItems(adjustedGroupItemsForSaving, newItemsToDisplay);
        }
      }
      
    }
  }

  const onDragEnd = (result) => {
    // `destination` is `undefined` if the item was dropped outside the list
    // In this case, do nothing
    setIsInitialLoad(false);
    if (!result.destination) {
      return;
    }

    const freshItems = reorder(
      selectedFeatures,
      result.source.index,
      result.destination.index
    );
    setSelectedFeatures(freshItems);
    setIsChangesMade(true);
  }

  const getListItems = (groupId) => {
    if(featureGroupItems?.length > 0){
      const itemsByGroup = featureGroupItems.filter(x => x[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID] === groupId);
      const filteredItems = itemsByGroup.sort((a, b) => (a[FieldNames.treatmentActivityGroupItems.sequenceNumber] > b[FieldNames.treatmentActivityGroupItems.sequenceNumber]) ? 1 : -1);
      
      if(filteredItems.length > 0){
        const groupForFilteredItem = (featureGroups && featureGroups.length > 0) ? featureGroups.filter(x => x[FieldNames.treatmentActivityGroups.id] === groupId) : [];
        const groupNameForFilteredItem = (groupForFilteredItem && groupForFilteredItem.length > 0) ? groupForFilteredItem[0][FieldNames.treatmentActivityGroups.treatmentActivityGroupName] : '';
        const groupNameForCard = (groupNameForFilteredItem !== groupName && groupId === selectedGroupId) ? groupName : groupNameForFilteredItem;
        return(
          <Fragment>
            <Grid item size={12} className='incident-list-sort-panel'>
              <label style={{ paddingLeft: 8 }}>{groupNameForCard}</label>
            </Grid>
            {filteredItems.map((item, i) => {
              return (
                <Grid item size={12} container direction="row" key={i}>
                  <Grid item size={10} className='incident-list-sort-panel'>
                    <p className="list-item-incident-name" style={{ width: '100%', paddingLeft: 8}}>
                      {(item[FieldNames.treatmentActivityGroupItems.entityType] === FieldNames.entityTypes.treatment) && 
                        `Treatment - ${item.Name}`
                      }
                      {(item[FieldNames.treatmentActivityGroupItems.entityType] === FieldNames.entityTypes.activity) && 
                        `Activity - ${item.Name}`
                      }
                      {item[FieldNames.treatmentActivityGroupItems.entityType] === FieldNames.entityTypes.actualTreatment && 
                        `Actual Treatment - ${item.Name}`
                      }
                      {item[FieldNames.treatmentActivityGroupItems.entityType] === FieldNames.entityTypes.actualActivity && 
                        `Actual Activity - ${item.Name}`
                      }
                    </p>
                  </Grid>
                  <Grid item size={2} className='incident-list-sort-panel'>
                    <p className="list-item-incident-name" style={{ textAlign: 'right'}}>{`${item[FieldNames.treatmentActivityGroupItems.sequenceNumber]} of ${filteredItems.length}`}</p>
                  </Grid>
                </Grid>
              )
            })}
          </Fragment>
        )
      } else {
        return('')
      }
    } else {
      return('')
    }
  }

    const getPageHeight = () => {
      if(containerRef && containerRef.current) {
        return containerRef.current?.clientHeight - 97;
      } 
    }

    const handleStay = () => {
      //We will remain on the same group
      setSnackbarWarningOpen(false);
    }

    const handleCloseCompletely = (isChanged) => {
      if(!isChanged){
        setSelectedGroupId(null);
        setSelectedFeatures([]);
        setAddOptions([]);
      }
      displayGroupingsClose(isChanged);
      setListItemSelected(false);
    }

    const handleLeaveWithoutChanges = () => {
      setIsChangesMade(false);
      if(nextSelectedIndex !== undefined && nextSelectedIndex !== null && nextSelectedGroupId){
        setSnackbarWarningOpen(false);
        handleListItemClick(nextSelectedIndex, nextSelectedGroupId, false);
      } else {
        setSnackbarWarningOpen(false);
        displayGroupingsClose(false);
        setSelectedFeatures([]);
        setAddOptions([])
        setListItemSelected(false);
      }
    }

    const updateFeatures = (selectedGroupFeatures, originalSelectedFeatures) => {
      let tempItems = [];

      if(selectedGroupFeatures && selectedGroupFeatures.length > 0){
        if(featureItems && featureItems.length > 0){
          tempItems = featureItems.map(feature => {
            let featureAttr = Object.assign({}, feature);
            let item = selectedGroupFeatures.filter(x => x[FieldNames.featureFields.objectID] === featureAttr[FieldNames.featureFields.objectID]);
            if(item && item.length > 0){
              let count = featureAttr[FieldNames.featureFields.groupCount];
              if(!featureAttr[FieldNames.featureFields.groupCount] || featureAttr[FieldNames.featureFields.groupCount] < 0){
                count = 0;
              }
              let existingOriginalGroupItems = originalSelectedFeatures.filter(x => x[FieldNames.featureFields.objectID] === featureAttr[FieldNames.featureFields.objectID]);
              if(!existingOriginalGroupItems || existingOriginalGroupItems.length === 0)
              {
                featureAttr[FieldNames.featureFields.groupCount] = count + 1;
                featureAttr[FieldNames.featureFields.hasGroup] = FieldNames.yesNo.yes;
              }              
            }
            return featureAttr
          });
          FeatureService.updateMultipleFeatures(tempItems).then(resp => {
            let isAtLeastPartiallySuccessful = isUpdatePartiallySuccessful(resp);
            let isFullySuccessful = isUpdateFullySuccessful(resp);
            if(isAtLeastPartiallySuccessful || isFullySuccessful){
              const tempTreatments = tempItems?.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.treatment);
              const tempActivities = tempItems?.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.activity);
              const tempActualTreatments = tempItems?.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.actualTreatment);
              const tempActualActivities = tempItems?.filter(x => x[FieldNames.featureFields.class] === FieldNames.classTypes.actualActivity);
              dispatch(setTreatments(tempTreatments));
              dispatch(setActivities(tempActivities));
              dispatch(setActualsTreatments(tempActualTreatments));
              dispatch(setActualsActivities(tempActualActivities));
            }
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          });
        }
      } 
    }

    const addItemsForGroupCall = (newTreatmentActivityGroupItems, itemReturnedForAdd) => {
      GroupingService.addItemsForGroup(newTreatmentActivityGroupItems).then(response => {
        //Now visually add it to the list ->
        let tempSelectedFeatures = _.cloneDeep(selectedFeatures);
        let originalSelectedFeatures = _.cloneDeep(selectedFeatures);
        const existingGroupItems = _.cloneDeep(featureGroupItems);

        if(response && response.successful && response.successful.length > 0){
          let featureItem;
          let count = 0;
          newTreatmentActivityGroupItems.forEach(newTreatmentActivityGroupItem => {
            if(newTreatmentActivityGroupItem){
              featureItem = featureItems.filter(x => x[FieldNames.featureFields.id] === newTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.joinID]);
              if((!featureItem || (featureItem && featureItem.length === 0)) && itemReturnedForAdd && itemReturnedForAdd.length > 0){
                featureItem = itemReturnedForAdd;
              }
            }

            if(featureItem && featureItem.length > 0){
              const currentSeqNumbers = selectedFeatures.map(x => x[FieldNames.treatmentActivityGroupItems.sequenceNumber]);
              const interimSeqNumbers = tempSelectedFeatures.map(x => x[FieldNames.treatmentActivityGroupItems.sequenceNumber]);
              const combinedSeqNumbers = [...currentSeqNumbers, ...interimSeqNumbers]; // Will have dupes, shouldn't matter due to max below
              const nextSequenceNumber = Math.max(...combinedSeqNumbers) + 1;
              const obj = createFeatureObject(featureItem[0], nextSequenceNumber);
              tempSelectedFeatures.push(obj);
              newTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.objectID] = response.successful[count];
              existingGroupItems.push(newTreatmentActivityGroupItem);
            }
            count++;
          })
          setSelectedFeatures(tempSelectedFeatures);
          setSelectionValue("");
          setFeatureGroupItems(existingGroupItems)
          let tempAddOptions = getAddOptions(existingGroupItems);
          setAddOptions(tempAddOptions);
          updateFeatures(tempSelectedFeatures, originalSelectedFeatures)
        }
      }).catch(error => {
        ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
      });
    }

    const handleOptionAddition = () => {
      if(selectionValue) {
        let newTreatmentActivityGroupItems = [];
        const itemsByGroup = featureGroupItems.filter(x => x[FieldNames.treatmentActivityGroupItems.treatmentActivityGroupJoinID] === selectedGroupId);
        const filteredItems = itemsByGroup.sort((a, b) => (a[FieldNames.treatmentActivityGroupItems.sequenceNumber] > b[FieldNames.treatmentActivityGroupItems.sequenceNumber]) ? 1 : -1);
        let newTreatmentActivityGroupItem = createNewTreatmentActivityGroupItem(filteredItems, selectionValue, null);
        newTreatmentActivityGroupItems.push(newTreatmentActivityGroupItem);
        if(newTreatmentActivityGroupItem[FieldNames.featureFields.hasActual] || 
          newTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.entityType] === FieldNames.entityTypes.actualActivity || 
          newTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.entityType] === FieldNames.entityTypes.actualTreatment){
            //We need to get either the actual or the estimated that goes with the selected Item
            const isEstimate = (newTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.entityType] === FieldNames.entityTypes.activity || 
              newTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.entityType] === FieldNames.entityTypes.treatment);
            const actionId = newTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.joinID];
            const actionFeature = newTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.joinFeature];

            PlannedActualsService.getAttachedFeatures(actionFeature, [actionId], isEstimate).then(resp => {
              if(resp && resp.length > 0){
                resp.forEach(featureItem => {
                  let newAttachedTreatmentActivityGroupItem = createNewTreatmentActivityGroupItem(filteredItems, featureItem, true);
                  let alreadyExists = false;
                  itemsByGroup.forEach(itemByGroup => {
                    if(itemByGroup[FieldNames.treatmentActivityGroupItems.entityID] === newAttachedTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.entityID] &&
                      itemByGroup[FieldNames.treatmentActivityGroupItems.entityType] === newAttachedTreatmentActivityGroupItem[FieldNames.treatmentActivityGroupItems.entityType]){
                        alreadyExists = true;
                      }
                  })

                  if(!alreadyExists){
                    newTreatmentActivityGroupItems.push(newAttachedTreatmentActivityGroupItem);
                  }
                });
                addItemsForGroupCall(newTreatmentActivityGroupItems, resp)

              } else {
                addItemsForGroupCall(newTreatmentActivityGroupItems, [])
              }
            }).catch(error => {
              ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
            });
        } else {
          addItemsForGroupCall(newTreatmentActivityGroupItems, [])
        }
      }
    }

  return (
    <div>
      <Dialog
        fullScreen
        open={groupingsOpen}
        onClose={() => handleCloseCompletely(isChangesMade)}
        TransitionComponent={Transition}
        ref={containerRef}
        style={{ overflow: 'none'}}
      >
        <Backdrop
          sx={{ color: ColorConstants.WhiteAlien, zIndex: (theme) => theme.zIndex.drawer + 1,}}
          open={showLoader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <AppBar sx={{ position: 'relative' }} style={{backgroundColor: ColorConstants.BlueAlien}}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => handleCloseCompletely(isChangesMade)}
              aria-label="close"
            >
              <CloseIcon style={{ fill: ColorConstants.WhiteAlien }} />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div" style={{ color: ColorConstants.WhiteAlien }}>
              Groupings
            </Typography>
            <Button variant="contained" size="small" autoFocus onClick={() => handleSaveGrouping()} style={{ backgroundColor: ColorConstants.WhiteAlien, color: ColorConstants.BlueAlien, borderColor: ColorConstants.WhiteAlien, marginRight: '10px'}}>
              Save
            </Button>
            <Button variant="contained" size="small" autoFocus onClick={() => handleCloseCompletely(isChangesMade)} style={{ backgroundColor: ColorConstants.WhiteAlien, color: ColorConstants.BlueAlien, borderColor: ColorConstants.WhiteAlien}}>
              Close
            </Button>
          </Toolbar>
          <div>
            <Dialog
              open={snackbarWarningOpen}
              onClose={() => handleStay()}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">
                {"There are unsaved changes."}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  Leave without saving changes?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => handleStay()}>Stay</Button>
                <Button onClick={() => handleLeaveWithoutChanges()} autoFocus>
                  Leave
                </Button>
              </DialogActions>
            </Dialog>
          </div>
          <Snackbar open={snackbarOpen} autoHideDuration={6000}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            onClose={() => {
              setSnackbarOpen(false);
              setSnackbarMessage("");
            }}>
            <Alert onClose={() => {
              setSnackbarOpen(false);
              setSnackbarMessage("");
            }} severity="success" sx={{ width: '100%' }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </AppBar>
        <Paper className={classes.flexPaper}>
        <Grid container spacing={1} sx={{ position: 'relative' }} className="content" style={{ height: getPageHeight() ? (getPageHeight() - 55) : 1}}>
          <Grid size={6} style={{ height: '100%', overflowY: 'scroll'}}>
            {(featureGroupIds.length > 0) &&
              <Fragment>
                <div style={{ width: '100%' }}>
                  <List>
                    {featureGroupIds.map((groupId, i) => (
                      <ListItemButton
                        key={i}
                        style={{ height: '1', width: '530px', display: 'block', justifyContent: 'flex-start', maxWidth: '530px', margin: '0px 0px 2px 0px' }}
                        className={i % 2 ? "ListItemOdd" : "ListItemEven"}
                        onClick={() => {handleListItemClick(i, groupId, isChangesMade)}}>
                        <Paper elevation={6} style={{ padding: 0}}>
                          <Box p={1}>
                            <Grid container spacing={1}>
                              <Grid size={0.75} style={{ padding: 0 }}>
                                <Box style={{ borderRadius: '5px 0 0 5px', backgroundColor: ColorConstants.GrayBoxAlien }} display="flex" height="1">
                                  <div style={{width: 30}}><p style={{ color: ColorConstants.WhiteAlien, width: 30, textAlign: 'center', fontWeight: 'bold', marginTop: 5}}></p></div>
                                </Box>
                              </Grid>
                              <Grid size={10} container onClick={(e) => {e.preventDefault();}} style={{ padding: 0 }}>
                                {getListItems(groupId)}                  
                              </Grid>
                              <Grid size={1.25} onClick={(e) => {e.preventDefault();}} style={{ padding: 0 }}>
                                <Box style={{ borderRadius: '0 5px 5px 0' }} display="flex" height="1">
                                  <ArrowForwardIosIcon fontSize='small' style={{
                                    color: "gray",
                                    height: "100%",
                                    margin: "0 5px 0 0"
                                  }} />
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        </Paper>
                      </ListItemButton>
                    ))}
                  </List>
                </div>
              </Fragment>
            }
          </Grid>
          <Grid size={6} className={classes.root} style={{ height: '100%', overflowX: 'scroll', overflowY: 'scroll'}}>
            <div>
              <Paper className={classes.flexPaper} style={{ overflowX: 'hidden', padding: 0, minHeight: "10px", width: '100%'}}>
                <DraggableList isAssociate={false} isInitialLoad={isInitialLoad} selectedFeatures={selectedFeatures} onDragEnd={onDragEnd} handleRemoval={handleRemoval} userInfo={userInfo} />
              </Paper>
            </div>
          </Grid>
        </Grid>
        </Paper>
        <DialogActions sx={{ position: 'fixed' }} style={{ height: 64, paddingTop: 10, bottom: 0, width: '100%', color: ColorConstants.WhiteAlien, boxShadow: '0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)' }}>
        {groupingsOpen && 
            <div style={{ display: 'flex', flexDirection: 'row', paddingRight: 10, height: 60}}>
              
              <ThemeProvider theme={theme}>
                <Autocomplete
                  id={`addFeature`}
                  variant="standard"
                  disabled={listItemSelected ? false : true}
                  value={selectionValue ? selectionValue : ""}
                  onChange={(event, newValue) => {
                    setSelectionValue(newValue);
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if(value === ""){
                      return true;
                    } else {
                      return option === value;
                    }
                  }}
                  getOptionLabel={(option) => { 
                    if(option){
                      return `${option[FieldNames.featureFields.name]} (${option[FieldNames.featureFields.objectID]})`;
                    } else {
                      return "";
                    }
                  }}
                  options={addOptions}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='standard'
                      sx={{minWidth: 270 }}
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {params?.InputProps?.endAdornment}
                            </React.Fragment>
                          )
                        }
                      }}
                    />
                  )}
                />
              </ThemeProvider>
              <Button variant="contained" disabled={listItemSelected ? false : true} size="small" autoFocus onClick={() => handleOptionAddition()} style={{ margin: 10}}>
                Add
              </Button>
              <BasicTextField
                  field="groupName"
                  label=""
                  value={groupName}
                  setValue={setGroupName}
                  onFieldChange={() => setIsChangesMade(true)}
                  variant={"standard"}
                  placeholder={"Enter group name"}
                  useFullWidth={true}
                  styleOverride={{ marginLeft: 15, minWidth: 270 }}
                  isDisabled = {listItemSelected ? false : true}
                />
              <Button variant="contained" size="small" autoFocus onClick={() => handleSaveGrouping()} style={{ margin: 10}}>
                Save
              </Button>
              <Button variant="contained" size="small" autoFocus onClick={() => handleCloseCompletely(isChangesMade)} style={{ margin: 10}}>
                Close
              </Button>
            </div>
        }
        </DialogActions>
      </Dialog>
    </div>
  );
}